import React, { memo, useCallback } from 'react';
import { NodeProps, NodeResizer, useReactFlow, Handle, Position } from 'reactflow';
import { Lock, Blend } from 'lucide-react';
import { Tooltip } from '@/components/ui/beui';
import { StickyNoteData } from '../../types';

// Border + text, independent of fill opacity.
const colors = {
  yellow: 'border-yellow-200 text-yellow-900 dark:border-yellow-700/50 dark:text-yellow-100',
  blue: 'border-blue-200 text-blue-900 dark:border-blue-700/50 dark:text-blue-100',
  green: 'border-green-200 text-green-900 dark:border-green-700/50 dark:text-green-100',
  pink: 'border-pink-200 text-pink-900 dark:border-pink-700/50 dark:text-pink-100',
  purple: 'border-purple-200 text-purple-900 dark:border-purple-700/50 dark:text-purple-100',
  red: 'border-red-200 text-red-900 dark:border-red-700/50 dark:text-red-100',
};

// Translucent notes composite against the canvas, so they shift with it. Solid
// notes don't — worth having now the dark canvas is near-black.
// Written out in full because Tailwind can't see interpolated class names.
const fills: Record<keyof typeof colors, { translucent: string; opaque: string }> = {
  yellow: { translucent: 'bg-yellow-100/70 dark:bg-yellow-900/40', opaque: 'bg-yellow-100 dark:bg-yellow-900' },
  blue: { translucent: 'bg-blue-100/70 dark:bg-blue-900/40', opaque: 'bg-blue-100 dark:bg-blue-900' },
  green: { translucent: 'bg-green-100/70 dark:bg-green-900/40', opaque: 'bg-green-100 dark:bg-green-900' },
  pink: { translucent: 'bg-pink-100/70 dark:bg-pink-900/40', opaque: 'bg-pink-100 dark:bg-pink-900' },
  purple: { translucent: 'bg-purple-100/70 dark:bg-purple-900/40', opaque: 'bg-purple-100 dark:bg-purple-900' },
  red: { translucent: 'bg-red-100/70 dark:bg-red-900/40', opaque: 'bg-red-100 dark:bg-red-900' },
};

const colorHex = {
  yellow: '#fef08a',
  blue: '#bfdbfe',
  green: '#bbf7d0',
  pink: '#fbcfe8',
  purple: '#e9d5ff',
  red: '#fecaca',
};

type ColorKey = keyof typeof colors;

const StickyNoteNode = ({ id, data, selected }: NodeProps<StickyNoteData>) => {
  const { setNodes } = useReactFlow();
  const colorKey: ColorKey = (data.color && data.color in colors) ? data.color : 'yellow';

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newVal = e.target.value;
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: { ...node.data, label: newVal },
            };
          }
          return node;
        })
      );
    },
    [id, setNodes]
  );

  const handleColorChange = useCallback(
    (color: ColorKey) => {
      if (data.onSnapshot) data.onSnapshot();
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: { ...node.data, color },
            };
          }
          return node;
        })
      );
    },
    [id, setNodes, data]
  );

  const handleToggleOpaque = useCallback(() => {
    if (data.onSnapshot) data.onSnapshot();
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, opaque: !node.data.opaque } } : node
      )
    );
  }, [id, setNodes, data]);

  const handleFocus = () => {
    if (data.onSnapshot) {
      data.onSnapshot();
    }
  };

  return (
    <>
      <NodeResizer
        minWidth={160}
        minHeight={100}
        isVisible={selected}
        lineClassName="border-primary opacity-50"
        handleClassName="h-3 w-3 bg-primary border-2 border-white rounded"
      />
      <div
        className={`group relative w-full h-full shadow-md rounded-none border-2 transition-all flex flex-col ${colors[colorKey]} ${
          data.opaque ? fills[colorKey].opaque : fills[colorKey].translucent
        }`}
        style={{
          boxShadow: selected ? `0 0 0 2px ${colorHex[colorKey]}80` : undefined,
        }}
      >
        {/* Lock indicator */}
        {data.locked && (
          <div className="absolute -top-2 -right-2 w-5 h-5 bg-amber-400 dark:bg-amber-500 rounded-full flex items-center justify-center shadow-sm z-50">
            <Lock className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Color Picker (Visible on Hover/Selected) */}
        <div
          className={`absolute -top-10 left-0 right-0 flex justify-center gap-1 transition-opacity z-50 ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          {(Object.keys(colors) as ColorKey[]).map((c) => (
            <Tooltip key={c} content={c}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => handleColorChange(c)}
                className={`w-6 h-6 rounded-full border-2 border-white dark:border-border shadow-sm transition-transform hover:scale-110 ${c === colorKey ? 'ring-2 ring-primary' : ''}`}
                style={{
                  backgroundColor: colorHex[c],
                }}
                aria-label={c}
              />
            </Tooltip>
          ))}

          <div className="mx-0.5 w-px self-stretch bg-border" />

          <Tooltip content={data.opaque ? 'Solid fill' : 'Translucent fill'}>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={handleToggleOpaque}
              aria-pressed={!!data.opaque}
              className={`grid h-6 w-6 place-items-center rounded-full border-2 border-white dark:border-border shadow-sm transition-transform hover:scale-110 ${
                data.opaque ? 'bg-foreground text-background' : 'bg-card text-muted-foreground'
              }`}
              aria-label={data.opaque ? 'Switch to translucent fill' : 'Switch to solid fill'}
            >
              <Blend className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </div>

        <textarea
          className="w-full h-full resize-none bg-transparent border-none focus:outline-none p-4 text-sm font-medium leading-relaxed custom-scrollbar placeholder:text-black/30 dark:placeholder:text-foreground/30"
          value={data.label}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="Write a note..."
          onKeyDown={(e) => e.stopPropagation()}
        />
        
        {/* Single handle per side - connectionMode="loose" allows bidirectional connections */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
          style={{
            backgroundColor: colorHex[colorKey],
            top: '-6px',
          }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
          style={{
            backgroundColor: colorHex[colorKey],
            left: '-6px',
          }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
          style={{
            backgroundColor: colorHex[colorKey],
            bottom: '-6px',
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
          style={{
            backgroundColor: colorHex[colorKey],
            right: '-6px',
          }}
        />
      </div>
    </>
  );
};

export default memo(StickyNoteNode);

